// SCR-STAFF-102 — پرونده مراجعه‌کننده /patients/:id (فاز 0): سربرگ پایه (نام، سن، شماره پرونده، نشان‌ها) و تب پروفایل: هویت و
// پروفایل (US-PAT-002؛ کد ملی فقط ۴ رقم آخر، نمایش کامل با Audit — C-AGENT-302)، منبع معرفی (قفل پس از اولین مراجعه —
// BR-PAT-072)، تماس‌ها (یک موبایل اصلی — US-PAT-004) و نشانی‌ها با GEO_DIVISIONS.
// AP-IAM-04: تب «پرونده بالینی» (سیاست دید بالینی و COMP-BREAK-GLASS — C-AGENT-400) و دکمه گزارش دسترسی به پرونده (SCR-STAFF-926).
// AP-PAT-02: سربرگ مشترک COMP-PATIENT-HEADER، تب «خلاصه و خط زمانی» (SCR-STAFF-103)، هویت تأییدشده و قفل فیلدها (US-PAT-020 تا
// 023)، فیلدهای سفارشی، برچسب، VIP، تصویر و کارت، فوت، تب خانواده (ولی، همراه، پرداخت‌کننده)، تب رضایت‌ها و ترجیحات، ادغام‌ها
// و تکمیل پرونده موقت.
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type AddressRequest,
  type ContactRequest,
  type PatientAddressView,
  type PatientContactView,
  type PatientView,
  type UpdatePatientRequest,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ConfirmDialog,
  DataTable,
  DetailPage,
  ErrorState,
  faDigits,
  FormDialog,
  isValidNationalId,
  KeyValueList,
  NationalIdFieldRHF,
  SelectRHF,
  TextFieldRHF,
  toJalali,
  useSaveShortcut,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ClinicalRecordPanel } from '../iam/ClinicalRecordPanel';
import { useGeoDivisions, type GeoDivisions } from '../settings/common';
import { formatJalaliInput, parseJalaliInput, patKeys, useReferralSources } from './common';
import { CompletePatientDialog } from './IncompletePatientsPage';
import { PatientMergesSection } from './MergeWizardPage';
import { PatientCertificatesPanel } from '../documents/CertificatesPanel';
import { PatientDocumentsPanel } from '../documents/PatientDocumentsPanel';
import { RecordExportPanel } from '../clinical/RecordExportPanel';
import { PatientFormsPanel } from '../documents/PatientFormsPanel';
import { PatientSignedConsentsPanel } from '../documents/PatientSignedConsentsPanel';
import { PatientConsentsPanel } from './PatientConsentsPanel';
import { PatientCustomFieldsPanel } from './PatientCustomFieldsPanel';
import { PatientExtrasPanel } from './PatientExtrasPanel';
import { PatientFamilyPanel } from './PatientFamilyPanel';
import { PatientHeader } from './PatientHeader';
import { PatientIdentityPanel } from './PatientIdentityPanel';
import { PatientTimeline } from './PatientTimeline';
import { PatientCoveragePanel } from '../insurance/PatientCoveragePanel';
import { PatientMessagesPanel } from '../messaging/PatientMessagesPanel';
import { PatientMedicalOrdersPanel } from '../medorders/PatientMedicalOrders';

export const SCR_STAFF_102 = 'SCR-STAFF-102';

const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed'] as const;
const EDUCATION = ['None', 'Primary', 'Secondary', 'Diploma', 'Associate', 'Bachelor', 'Master', 'Doctorate'] as const;
const BLOOD = ['APositive', 'ANegative', 'BPositive', 'BNegative', 'AbPositive', 'AbNegative', 'OPositive', 'ONegative'] as const;

const profileSchema = z
  .object({
    firstName: zRequiredText(100),
    lastName: z.string().max(100, { error: 'common:validation.too_long' }),
    temporary: z.boolean(),
    identityChangeReason: z.string().max(500, { error: 'common:validation.too_long' }),
    gender: z.enum(['Female', 'Male']),
    birthDate: z.string(),
    fatherName: z.string().max(100, { error: 'common:validation.too_long' }),
    birthPlace: z.string().max(100, { error: 'common:validation.too_long' }),
    maritalStatus: z.string(),
    education: z.string(),
    occupation: z.string().max(100, { error: 'common:validation.too_long' }),
    nationality: z.string().max(2, { error: 'common:validation.too_long' }),
    language: z.string().max(8, { error: 'common:validation.too_long' }),
    bloodGroup: z.string(),
    adminNote: z.string().max(1000, { error: 'common:validation.too_long' }),
    nationalId: z.string(),
    removeNationalId: z.boolean(),
    referralSourceId: z.string(),
  })
  .superRefine((value, context) => {
    if (!value.temporary && !value.lastName.trim()) {
      context.addIssue({ code: 'custom', path: ['lastName'], message: 'common:validation.required' });
    }
    if (value.nationalId && !isValidNationalId(value.nationalId)) {
      context.addIssue({ code: 'custom', path: ['nationalId'], message: 'common:validation.national_id' });
    }
    if (value.birthDate.trim() && !parseJalaliInput(value.birthDate)) {
      context.addIssue({ code: 'custom', path: ['birthDate'], message: 'pat:validation.birth_date' });
    }
  });
type ProfileForm = z.input<typeof profileSchema>;
const PROFILE_FIELDS = ['firstName', 'lastName', 'gender', 'birthDate', 'nationalId', 'referralSourceId', 'identityChangeReason'] as const;
/** فیلدهای هویتی قفل‌شونده پس از تأیید (US-PAT-023) ← نام فیلد فرم. */
const LOCKABLE: Record<string, 'firstName' | 'lastName' | 'fatherName' | 'birthDate' | 'nationalId'> = {
  first_name: 'firstName',
  last_name: 'lastName',
  father_name: 'fatherName',
  birth_date: 'birthDate',
  national_id: 'nationalId',
};

function toForm(patient: PatientView): ProfileForm {
  return {
    firstName: patient.firstName,
    lastName: patient.lastName,
    temporary: patient.isTemporary,
    identityChangeReason: '',
    gender: patient.gender,
    birthDate: formatJalaliInput(patient.birthDate),
    fatherName: patient.fatherName ?? '',
    birthPlace: patient.birthPlace ?? '',
    maritalStatus: patient.maritalStatus ?? '',
    education: patient.education ?? '',
    occupation: patient.occupation ?? '',
    nationality: patient.nationality,
    language: patient.language,
    bloodGroup: patient.bloodGroup ?? '',
    adminNote: patient.adminNote ?? '',
    nationalId: '',
    removeNationalId: false,
    referralSourceId: patient.referralSourceId ?? '',
  };
}

function usePatientCache(id: string) {
  const { session } = useTenantSession();
  const queryClient = useQueryClient();
  return (patient: PatientView) => {
    queryClient.setQueryData(patKeys.patient(session?.tenantId, id), patient);
    void queryClient.invalidateQueries({ queryKey: patKeys.summary(session?.tenantId, id) });
    void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'search'] });
  };
}

function NationalIdView({ patient, canReveal }: { patient: PatientView; canReveal: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const reveal = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/national-id', { params: { path: { id: patient.id } } })),
  });
  if (!patient.hasNationalId) {
    return <span>{t('pat:profile.national_id_none')}</span>;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" component="span">
      <bdi dir="ltr" data-testid="national-id-value">
        {reveal.data ? faDigits(reveal.data.nationalId) : t('pat:profile.national_id_masked', { last4: faDigits(patient.nationalIdLast4 ?? '') })}
      </bdi>
      {canReveal && !reveal.data && (
        <Tooltip title={t('pat:profile.show_national_id')}>
          <IconButton size="small" aria-label={t('pat:profile.show_national_id')} onClick={() => reveal.mutate()} data-testid="national-id-reveal">
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function IdentitySection({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const updateCache = usePatientCache(patient.id);
  const sources = useReferralSources(true);
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const values = useMemo(() => toForm(patient), [patient]);
  const { control, handleSubmit, setError, formState, reset } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    values,
  });
  const remove = useWatch({ control, name: 'removeNationalId' });
  const identity = patient.identity;
  const lockedFields = new Set(identity.locked ? identity.lockedFields.map((field) => LOCKABLE[field]).filter(Boolean) : []);
  const isLocked = (field: string) => lockedFields.has(field as never) && !identity.canOverride;

  const save = useMutation({
    mutationFn: (body: UpdatePatientRequest) =>
      unwrap(tenantApi.PUT('/api/v1/patients/patients/{id}', { params: { path: { id: patient.id } }, body })),
    onMutate: () => {
      setSubmitError(undefined);
      setSaved(false);
    },
    onSuccess: (updated) => {
      updateCache(updated);
      reset(toForm(updated));
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: PROFILE_FIELDS,
        codeFields: {
          'PHX-PAT-0101': 'nationalId',
          'PHX-PAT-0301': 'nationalId',
          'PHX-PAT-0102': 'birthDate',
          'PHX-PAT-0601': 'referralSourceId',
          'PHX-PAT-0602': 'referralSourceId',
          'PHX-PAT-0104': 'identityChangeReason',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = handleSubmit((form) =>
    save.mutate({
      firstName: form.firstName,
      lastName: form.lastName,
      gender: form.gender,
      birthDate: parseJalaliInput(form.birthDate) ?? null,
      fatherName: form.fatherName || null,
      birthPlace: form.birthPlace || null,
      maritalStatus: (form.maritalStatus || null) as UpdatePatientRequest['maritalStatus'],
      education: (form.education || null) as UpdatePatientRequest['education'],
      occupation: form.occupation || null,
      nationality: form.nationality || null,
      language: form.language || null,
      bloodGroup: (form.bloodGroup || null) as UpdatePatientRequest['bloodGroup'],
      adminNote: form.adminNote || null,
      nationalId: form.removeNationalId ? null : form.nationalId || null,
      removeNationalId: form.removeNationalId,
      referralSourceId: form.referralSourceId || null,
      referrerPatientId: form.referralSourceId === (patient.referralSourceId ?? '') ? patient.referrerPatientId : null,
      identityChangeReason: form.identityChangeReason.trim() || null,
      rowVersion: patient.rowVersion,
    }),
  );
  useSaveShortcut(() => void submit(), canWrite && !save.isPending && formState.isDirty);

  const options = (keys: readonly string[], prefix: string) => [
    { value: '', labelKey: 'pat:fields.none' },
    ...keys.map((key) => ({ value: key, labelKey: `pat:${prefix}.${key}` })),
  ];
  const sourceOptions = [
    { value: '', labelKey: 'pat:fields.none' },
    ...(sources.data ?? [])
      .filter((source) => source.isActive || source.id === patient.referralSourceId)
      .map((source) => ({ value: source.id, label: source.name })),
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <Typography variant="h2" gutterBottom>
          {t('pat:profile.sections.identity')}
        </Typography>
        <Stack spacing={2}>
          {saved && <Alert severity="success">{t('pat:profile.saved')}</Alert>}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF control={control} name="firstName" labelKey="pat:fields.first_name" required fullWidth disabled={!canWrite || isLocked('firstName')} />
            <TextFieldRHF
              control={control}
              name="lastName"
              labelKey="pat:fields.last_name"
              required={!patient.isTemporary}
              fullWidth
              disabled={!canWrite || isLocked('lastName')}
            />
            <SelectRHF
              control={control}
              name="gender"
              labelKey="pat:fields.gender"
              required
              fullWidth
              disabled={!canWrite}
              options={[
                { value: 'Female', labelKey: 'pat:gender.Female' },
                { value: 'Male', labelKey: 'pat:gender.Male' },
              ]}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF
              control={control}
              name="birthDate"
              labelKey="pat:fields.birth_date"
              helperKey={patient.birthDateEstimated ? 'pat:profile.birth_date_estimated' : 'pat:fields.birth_date_help'}
              fullWidth
              disabled={!canWrite || isLocked('birthDate')}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
            />
            <TextFieldRHF control={control} name="fatherName" labelKey="pat:fields.father_name" fullWidth disabled={!canWrite || isLocked('fatherName')} />
            <TextFieldRHF control={control} name="birthPlace" labelKey="pat:fields.birth_place" fullWidth disabled={!canWrite} />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <SelectRHF control={control} name="maritalStatus" labelKey="pat:fields.marital_status" fullWidth disabled={!canWrite} options={options(MARITAL, 'marital')} />
            <SelectRHF control={control} name="education" labelKey="pat:fields.education" fullWidth disabled={!canWrite} options={options(EDUCATION, 'education')} />
            <TextFieldRHF control={control} name="occupation" labelKey="pat:fields.occupation" fullWidth disabled={!canWrite} />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF control={control} name="nationality" labelKey="pat:fields.nationality" fullWidth disabled={!canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextFieldRHF control={control} name="language" labelKey="pat:fields.language" fullWidth disabled={!canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <SelectRHF control={control} name="bloodGroup" labelKey="pat:fields.blood_group" fullWidth disabled={!canWrite} options={options(BLOOD, 'blood')} />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                {t('pat:fields.national_id')}
              </Typography>
              <NationalIdView patient={patient} canReveal={canWrite} />
            </Box>
            {canWrite && !isLocked('nationalId') && (
              <>
                <NationalIdFieldRHF
                  control={control}
                  name="nationalId"
                  labelKey="pat:profile.national_id_new"
                  helperKey="pat:profile.national_id_new_help"
                  fullWidth
                  disabled={remove === true}
                />
                {patient.hasNationalId && (
                  <CheckboxRHF control={control} name="removeNationalId" labelKey="pat:profile.remove_national_id" />
                )}
              </>
            )}
          </Stack>
          {canWrite && identity.locked && identity.canOverride && (
            <TextFieldRHF
              control={control}
              name="identityChangeReason"
              labelKey="pat:identity.change_reason"
              helperKey="pat:identity.change_reason_help"
              fullWidth
            />
          )}
          <Typography variant="subtitle1">{t('pat:profile.sections.referral')}</Typography>
          {patient.referralLocked && <Alert severity="info">{t('pat:profile.referral_locked')}</Alert>}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="referralSourceId"
              labelKey="pat:fields.referral_source"
              fullWidth
              disabled={!canWrite || patient.referralLocked}
              options={sourceOptions}
            />
            {patient.referringPhysicianName && (
              <KeyValueList columns={1} items={[{ labelKey: 'pat:fields.referring_physician', value: patient.referringPhysicianName }]} />
            )}
            {patient.referrerDisplayName && (
              <KeyValueList columns={1} items={[{ labelKey: 'pat:fields.referrer', value: patient.referrerDisplayName }]} />
            )}
          </Stack>
          <TextFieldRHF
            control={control}
            name="adminNote"
            labelKey="pat:fields.admin_note"
            helperKey="pat:fields.admin_note_help"
            fullWidth
            multiline
            minRows={2}
            disabled={!canWrite}
          />
          {submitError ? <ErrorState error={submitError} compact /> : null}
          {canWrite && (
            <Box>
              <Button type="submit" variant="contained" disabled={save.isPending || !formState.isDirty} data-testid="profile-save">
                {t('common:actions.save')}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

const contactSchema = z
  .object({
    type: z.enum(['Mobile', 'Landline', 'Email']),
    value: z.string().min(1, { error: 'common:validation.required' }).max(254, { error: 'common:validation.too_long' }),
    owner: z.enum(['Self', 'Guardian', 'Spouse', 'Other']),
    isPrimary: z.boolean(),
  });
type ContactForm = z.input<typeof contactSchema>;

function ContactDialog({
  patientId,
  contact,
  open,
  onClose,
}: {
  patientId: string;
  contact: PatientContactView | null;
  open: boolean;
  onClose: () => void;
}) {
  const { tenantApi } = useTenantSession();
  const updateCache = usePatientCache(patientId);
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = contact === null;
  const values = useMemo<ContactForm>(
    () => ({
      type: contact?.type ?? 'Mobile',
      value: contact?.value ?? '',
      owner: contact?.owner ?? 'Self',
      isPrimary: contact?.isPrimary ?? false,
    }),
    [contact],
  );
  const { control, handleSubmit, formState, setError, reset } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    values,
  });
  const type = useWatch({ control, name: 'type' });
  useEffect(() => {
    if (open) {
      setSubmitError(undefined);
    }
  }, [open]);

  const save = useMutation({
    mutationFn: (body: ContactRequest) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/patients/patients/{id}/contacts', {
              params: { path: { id: patientId } },
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/patients/patients/{id}/contacts/{contactId}', {
              params: { path: { id: patientId, contactId: contact.id } },
              body,
            }),
          ),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (patient) => {
      updateCache(patient);
      reset();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['type', 'value', 'owner', 'isPrimary'],
        codeFields: { 'PHX-PAT-0401': 'isPrimary', 'PHX-PAT-0402': 'value' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open={open}
      titleKey={creating ? 'pat:contacts.add' : 'pat:contacts.edit'}
      screenId="SCR-STAFF-102-CONTACT"
      onSubmit={() => void handleSubmit((form) => save.mutate({ ...form, isPrimary: form.type === 'Mobile' && form.isPrimary }))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={control}
        name="type"
        labelKey="pat:contacts.type"
        fullWidth
        disabled={!creating}
        options={(['Mobile', 'Landline', 'Email'] as const).map((value) => ({ value, labelKey: `pat:contacts.types.${value}` }))}
      />
      <TextFieldRHF control={control} name="value" labelKey="pat:contacts.value" required fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
      <SelectRHF
        control={control}
        name="owner"
        labelKey="pat:contacts.owner_label"
        fullWidth
        options={(['Self', 'Guardian', 'Spouse', 'Other'] as const).map((value) => ({ value, labelKey: `pat:contacts.owner.${value}` }))}
      />
      {type === 'Mobile' && (
        <CheckboxRHF control={control} name="isPrimary" labelKey="pat:contacts.is_primary" helperKey="pat:contacts.primary_change_hint" />
      )}
    </FormDialog>
  );
}

function ContactsSection({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const updateCache = usePatientCache(patient.id);
  const [editing, setEditing] = useState<PatientContactView | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<PatientContactView | null>(null);
  const remove = useMutation({
    mutationFn: (contact: PatientContactView) =>
      unwrap(
        tenantApi.DELETE('/api/v1/patients/patients/{id}/contacts/{contactId}', {
          params: { path: { id: patient.id, contactId: contact.id } },
        }),
      ),
    onSuccess: (updated) => {
      updateCache(updated);
      setDeleting(null);
    },
  });

  const columns: DataTableColumn<PatientContactView>[] = [
    { id: 'type', headerKey: 'pat:contacts.type', cell: (row) => t(`pat:contacts.types.${row.type}`) },
    {
      id: 'value',
      headerKey: 'pat:contacts.value',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <bdi dir="ltr">{row.type === 'Email' ? row.value : faDigits(row.value)}</bdi>
          {row.isPrimary && <Chip size="small" color="primary" label={t('pat:contacts.primary')} data-testid="contact-primary" />}
        </Stack>
      ),
    },
    { id: 'owner', headerKey: 'pat:contacts.owner_label', cell: (row) => t(`pat:contacts.owner.${row.owner}`) },
    { id: 'verification', headerKey: 'pat:contacts.verification_label', cell: (row) => t(`pat:contacts.verification.${row.verification}`) },
    {
      id: 'actions',
      headerKey: 'pat:contacts.edit',
      cell: (row) =>
        canWrite ? (
          <Stack direction="row" spacing={0.5}>
            <IconButton aria-label={t('pat:contacts.edit')} onClick={() => setEditing(row)}>
              <EditOutlinedIcon />
            </IconButton>
            {!row.isPrimary && (
              <IconButton aria-label={t('pat:contacts.delete')} onClick={() => setDeleting(row)}>
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </Stack>
        ) : null,
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:profile.sections.contacts')}
        </Typography>
        {canWrite && (
          <Button startIcon={<AddIcon />} onClick={() => setEditing(null)} data-testid="contact-add">
            {t('pat:contacts.add')}
          </Button>
        )}
      </Stack>
      <DataTable captionKey="pat:profile.sections.contacts" columns={columns} rows={patient.contacts} getRowId={(row) => row.id} />
      <ContactDialog patientId={patient.id} contact={editing ?? null} open={editing !== undefined} onClose={() => setEditing(undefined)} />
      <ConfirmDialog
        open={deleting !== null}
        titleKey="pat:contacts.delete"
        messageKey="pat:contacts.delete_confirm"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Paper>
  );
}

const addressSchema = z.object({
  type: z.enum(['Home', 'Work']),
  provinceCode: z.string(),
  cityCode: z.string(),
  line: z.string().max(500, { error: 'common:validation.too_long' }),
  postalCode: z.string().max(10, { error: 'common:validation.too_long' }),
});
type AddressForm = z.input<typeof addressSchema>;

function AddressDialog({
  patientId,
  address,
  open,
  geo,
  onClose,
}: {
  patientId: string;
  address: PatientAddressView | null;
  open: boolean;
  geo: GeoDivisions;
  onClose: () => void;
}) {
  const { tenantApi } = useTenantSession();
  const updateCache = usePatientCache(patientId);
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = address === null;
  const values = useMemo<AddressForm>(
    () => ({
      type: address?.type ?? 'Home',
      provinceCode: address?.provinceCode ?? '',
      cityCode: address?.cityCode ?? '',
      line: address?.line ?? '',
      postalCode: address?.postalCode ?? '',
    }),
    [address],
  );
  const { control, handleSubmit, formState, setError, reset, setValue } = useForm<AddressForm>({
    resolver: zodResolver(addressSchema),
    values,
  });
  const province = useWatch({ control, name: 'provinceCode' });
  const city = useWatch({ control, name: 'cityCode' });
  const cities = geo.citiesOf(province);
  useEffect(() => {
    if (city && !cities.some((item) => item.key === city)) {
      setValue('cityCode', '', { shouldDirty: true });
    }
  }, [city, cities, setValue]);

  const save = useMutation({
    mutationFn: (body: AddressRequest) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/patients/patients/{id}/addresses', {
              params: { path: { id: patientId } },
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/patients/patients/{id}/addresses/{addressId}', {
              params: { path: { id: patientId, addressId: address.id } },
              body,
            }),
          ),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (patient) => {
      updateCache(patient);
      reset();
      onClose();
    },
    onError: (error) => {
      let applied = applyServerErrors(error, setError, { fields: ['type', 'provinceCode', 'cityCode', 'line', 'postalCode'] });
      if (isApiError(error) && typeof error.extensions['field'] === 'string') {
        const field = error.extensions['field'];
        if (field === 'provinceCode' || field === 'cityCode' || field === 'postalCode') {
          setError(field, {
            type: 'server',
            message: field === 'postalCode' ? 'errors:validation.postal_code' : 'errors:validation.geo_division',
          });
          applied += 1;
        }
      }
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open={open}
      titleKey={creating ? 'pat:addresses.add' : 'pat:addresses.edit'}
      screenId="SCR-STAFF-102-ADDRESS"
      onSubmit={() =>
        void handleSubmit((form) =>
          save.mutate({
            type: form.type,
            provinceCode: form.provinceCode || null,
            cityCode: form.cityCode || null,
            line: form.line || null,
            postalCode: form.postalCode || null,
          }),
        )()
      }
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={control}
        name="type"
        labelKey="pat:addresses.type"
        fullWidth
        options={(['Home', 'Work'] as const).map((value) => ({ value, labelKey: `pat:addresses.types.${value}` }))}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="provinceCode"
          labelKey="pat:addresses.province"
          fullWidth
          options={[{ value: '', labelKey: 'pat:fields.none' }, ...geo.provinces.map((p) => ({ value: p.key, label: p.name }))]}
        />
        <SelectRHF
          control={control}
          name="cityCode"
          labelKey="pat:addresses.city"
          fullWidth
          disabled={!province}
          options={[{ value: '', labelKey: 'pat:fields.none' }, ...cities.map((c) => ({ value: c.key, label: c.name }))]}
        />
      </Stack>
      <TextFieldRHF control={control} name="line" labelKey="pat:addresses.line" fullWidth multiline minRows={2} />
      <TextFieldRHF control={control} name="postalCode" labelKey="pat:addresses.postal_code" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
    </FormDialog>
  );
}

function AddressesSection({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const updateCache = usePatientCache(patient.id);
  const { geo } = useGeoDivisions();
  const [editing, setEditing] = useState<PatientAddressView | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<PatientAddressView | null>(null);
  const remove = useMutation({
    mutationFn: (address: PatientAddressView) =>
      unwrap(
        tenantApi.DELETE('/api/v1/patients/patients/{id}/addresses/{addressId}', {
          params: { path: { id: patient.id, addressId: address.id } },
        }),
      ),
    onSuccess: (updated) => {
      updateCache(updated);
      setDeleting(null);
    },
  });

  const columns: DataTableColumn<PatientAddressView>[] = [
    { id: 'type', headerKey: 'pat:addresses.type', cell: (row) => t(`pat:addresses.types.${row.type}`) },
    {
      id: 'location',
      headerKey: 'pat:addresses.city',
      cell: (row) => [geo.name(row.provinceCode), geo.name(row.cityCode)].filter(Boolean).join(' / ') || t('common:states.none'),
    },
    { id: 'line', headerKey: 'pat:addresses.line', cell: (row) => row.line ?? t('common:states.none') },
    {
      id: 'postal',
      headerKey: 'pat:addresses.postal_code',
      cell: (row) => (row.postalCode ? <bdi dir="ltr">{faDigits(row.postalCode)}</bdi> : t('common:states.none')),
    },
    {
      id: 'actions',
      headerKey: 'pat:addresses.edit',
      cell: (row) =>
        canWrite ? (
          <Stack direction="row" spacing={0.5}>
            <IconButton aria-label={t('pat:addresses.edit')} onClick={() => setEditing(row)}>
              <EditOutlinedIcon />
            </IconButton>
            <IconButton aria-label={t('pat:addresses.delete')} onClick={() => setDeleting(row)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:profile.sections.addresses')}
        </Typography>
        {canWrite && (
          <Button startIcon={<AddIcon />} onClick={() => setEditing(null)} data-testid="address-add">
            {t('pat:addresses.add')}
          </Button>
        )}
      </Stack>
      <DataTable
        captionKey="pat:profile.sections.addresses"
        columns={columns}
        rows={patient.addresses}
        getRowId={(row) => row.id}
        emptyTitleKey="pat:addresses.empty"
      />
      <AddressDialog patientId={patient.id} address={editing ?? null} open={editing !== undefined} geo={geo} onClose={() => setEditing(undefined)} />
      <ConfirmDialog
        open={deleting !== null}
        titleKey="pat:addresses.delete"
        messageKey="pat:addresses.delete_confirm"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Paper>
  );
}

const TABS = ['summary', 'profile', 'family', 'consents', 'coverage', 'documents', 'forms', 'signed-consents', 'certificates', 'messages', 'clinical', 'record-export', 'medorders'] as const;

export function PatientProfilePage() {
  const { t } = useTranslation('pat');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('pat.patient.update');
  const guardian = useWriteAccess('pat.guardian.manage');
  const consent = useWriteAccess('pat.consent.manage');
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = TABS.includes(requested as (typeof TABS)[number]) ? (requested as string) : 'profile';
  const [completing, setCompleting] = useState(false);
  const patient = useQuery({
    queryKey: patKeys.patient(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}', { params: { path: { id } } })),
    enabled: access.can('pat.patient.view') && id.length > 0,
  });
  const data = patient.data;

  return (
    <>
    <DetailPage
      screenId={SCR_STAFF_102}
      isLoading={patient.isPending}
      error={patient.error}
      onRetry={() => void patient.refetch()}
      titleKey="pat:profile.heading"
      titleValues={{ name: data?.displayName ?? '' }}
      banner={data ? <PatientHeader patientId={data.id} /> : undefined}
      actions={
        data && access.can('iam.audit.view_patient_access') ? (
          <Button component={RouterLink} to={`/patients/${data.id}/access-report`} variant="outlined" data-testid="patient-access-report">
            {t('iam:clinical.access_report')}
          </Button>
        ) : undefined
      }
      activeTab={tab}
      onTabChange={(next) => {
        const query = new URLSearchParams(params);
        query.set('tab', next);
        setParams(query, { replace: true });
      }}
      tabs={
        data
          ? [
              { id: 'summary', labelKey: 'pat:profile.tabs.summary', content: <PatientTimeline patientId={data.id} /> },
              {
                id: 'profile',
                labelKey: 'pat:profile.tabs.profile',
                content: (
                  <Stack spacing={2}>
                    {data.isTemporary && (
                      <Alert
                        severity="warning"
                        data-testid="temporary-alert"
                        action={
                          write.canWrite ? (
                            <Button color="inherit" size="small" onClick={() => setCompleting(true)} data-testid="temporary-complete">
                              {t('pat:temporary.complete')}
                            </Button>
                          ) : undefined
                        }
                      >
                        {t('pat:temporary.notice')}
                      </Alert>
                    )}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <KeyValueList
                        columns={3}
                        items={[
                          { labelKey: 'pat:profile.file_number', value: <bdi dir="ltr">{faDigits(data.fileNumber)}</bdi> },
                          { labelKey: 'pat:profile.previous_file_number', value: data.previousFileNumber ? faDigits(data.previousFileNumber) : null },
                          { labelKey: 'pat:profile.registered_at', value: toJalali(data.createdAt) },
                          { labelKey: 'pat:profile.visit_count', value: faDigits(data.visitCount) },
                          { labelKey: 'pat:profile.first_visit', value: data.firstVisitAt ? toJalali(data.firstVisitAt) : null },
                          { labelKey: 'pat:profile.last_visit', value: data.lastVisitAt ? toJalali(data.lastVisitAt) : null },
                        ]}
                      />
                    </Paper>
                    <PatientIdentityPanel patient={data} canWrite={write.canWrite} />
                    <IdentitySection patient={data} canWrite={write.canWrite} />
                    <ContactsSection patient={data} canWrite={write.canWrite} />
                    <AddressesSection patient={data} canWrite={write.canWrite} />
                    <PatientCustomFieldsPanel patientId={data.id} />
                    <PatientExtrasPanel patient={data} />
                    <PatientMergesSection patientId={data.id} mergedIntoId={data.mergedIntoId} />
                  </Stack>
                ),
              },
              {
                id: 'family',
                labelKey: 'pat:profile.tabs.family',
                content: <PatientFamilyPanel patient={data} canManage={guardian.canWrite} canWrite={write.canWrite} />,
              },
              {
                id: 'consents',
                labelKey: 'pat:profile.tabs.consents',
                content: <PatientConsentsPanel patient={data} canManage={consent.canWrite} />,
              },
              // AP-DOC-01: اسناد (SCR-STAFF-600)، فرم‌ها (SCR-STAFF-602) و رضایت‌نامه‌های امضاشده (SCR-STAFF-604).
              ...(access.can('doc.document.view')
                ? [
                    { id: 'documents', labelKey: 'doc:patient.tab_documents', content: <PatientDocumentsPanel patientId={data.id} /> },
                    { id: 'forms', labelKey: 'doc:patient.tab_forms', content: <PatientFormsPanel patientId={data.id} /> },
                    { id: 'signed-consents', labelKey: 'doc:patient.tab_consents', content: <PatientSignedConsentsPanel patientId={data.id} /> },
                    // AP-CLN-04: گواهی و نامه با QR (SCR-STAFF-606؛ US-DOC-052).
                    { id: 'certificates', labelKey: 'doc:certificate.tab', content: <PatientCertificatesPanel patientId={data.id} /> },
                  ]
                : []),
              // AP-INS-01: COMP-PATIENT-COVERAGE (پوشش بیمه‌ای، استعلام استحقاق و تأیید دستی).
              ...(access.can('ins.coverage.manage')
                ? [{ id: 'coverage', labelKey: 'ins:coverages.title', content: <PatientCoveragePanel patientId={data.id} /> }]
                : []),
              // AP-MSG-01: COMP-PATIENT-MESSAGES (تاریخچه با msg.log.view، ارسال با msg.send.manual).
              ...(access.can('msg.log.view') || access.can('msg.send.manual')
                ? [{ id: 'messages', labelKey: 'msg:patient.tab', content: <PatientMessagesPanel patientId={data.id} /> }]
                : []),
              ...(access.can('cln.record.view')
                ? [{ id: 'clinical', labelKey: 'iam:clinical.tab', content: <ClinicalRecordPanel patientId={data.id} /> }]
                : []),
              // AP-CLN-04: خروجی پرونده (SCR-STAFF-510؛ US-CLN-101).
              ...(access.can('cln.record.export')
                ? [{ id: 'record-export', labelKey: 'cln:record_export.tab', content: <RecordExportPanel patientId={data.id} /> }]
                : []),
              // AP-MED-01: نسخه، درخواست و نتیجه، ارجاع (SCR-STAFF-700، 702، 705)؛ پذیرش فقط با Permissionهای عملیاتی (C-AGENT-871، C-AGENT-1050).
              ...(['med.prescription.view', 'med.prescription.print', 'med.order.view', 'med.order.track', 'med.referral.respond', 'med.referral.schedule'].some((code) => access.can(code))
                ? [{ id: 'medorders', labelKey: 'med:patient.tab', content: <PatientMedicalOrdersPanel patientId={data.id} /> }]
                : []),
            ]
          : []
      }
    />
    <CompletePatientDialog patient={completing && data ? data : null} onClose={() => setCompleting(false)} />
    </>
  );
}
